import { useRef } from 'react'

import classNames from 'classnames'
import { CalendarDays } from 'lucide-react'
import DatePicker from 'react-datepicker'
import { Controller, useWatch, type UseFormReturn } from 'react-hook-form'

import 'react-datepicker/dist/react-datepicker.css'

import type { CreateTournamentInput } from '../types'
import { FieldError, FieldLabel, Help, Section, inputClass } from '../ui'

const toDate = (value?: string) => {
  if (!value) return null
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())

export const ScheduleStep = ({ form }: { form: UseFormReturn<CreateTournamentInput> }) => {
  const {
    control,
    formState: { errors },
  } = form
  const startDate = useWatch({ control, name: 'startDate' })
  const start = toDate(startDate)
  // Escape should close an open calendar, not the whole Create tournament dialog.
  const calendarOpen = useRef(false)

  const dateField = (name: 'startDate' | 'endDate', label: string, help: string) => (
    <div data-field={name}>
      <FieldLabel htmlFor={`ct-${name}`}>{label}</FieldLabel>
      <Controller
        control={control}
        name={name}
        rules={{
          required: name === 'startDate' ? 'Pick a start date.' : 'Pick an end date.',
          validate:
            name === 'endDate'
              ? (value, all) => {
                  const end = toDate(value)
                  const s = toDate(all.startDate)
                  return !end || !s || startOfDay(end) >= startOfDay(s) || 'The end date can’t be before the start date.'
                }
              : undefined,
        }}
        render={({ field }) => (
          <div
            className="relative"
            onKeyDown={(e) => {
              if (e.key === 'Escape' && calendarOpen.current) e.stopPropagation()
            }}
          >
            <CalendarDays
              size={16}
              aria-hidden
              className="pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-light-typo-gray dark:text-dark-typo-gray"
            />
            <DatePicker
              id={`ct-${name}`}
              name={field.name}
              selected={toDate(field.value)}
              onChange={(value: Date | null) => {
                // Stored as a date string, exactly as before (time of day is not collected).
                field.onChange(value ? value.toDateString() : '')
              }}
              onBlur={field.onBlur}
              onCalendarOpen={() => {
                calendarOpen.current = true
              }}
              onCalendarClose={() => {
                // Let the Escape keydown finish before the dialog may handle Escape again.
                setTimeout(() => {
                  calendarOpen.current = false
                }, 0)
              }}
              minDate={name === 'endDate' && start ? start : undefined}
              placeholderText="Select a date"
              dateFormat="EEE, MMM d, yyyy"
              autoComplete="off"
              ariaInvalid={errors[name] ? 'true' : undefined}
              wrapperClassName="w-full ct-date"
              className={classNames(inputClass(!!errors[name]), 'pl-10')}
              calendarClassName="ct-calendar"
              popperPlacement="bottom-start"
            />
          </div>
        )}
      />
      {errors[name] ? <FieldError message={errors[name]?.message} /> : <Help>{help}</Help>}
    </div>
  )

  return (
    <div>
      <Section>
        <div className="grid gap-5 sm:grid-cols-2">
          {dateField('startDate', 'Start date', 'Shown on the card. Matches can begin from this day.')}
          {dateField('endDate', 'End date', 'The last day matches are played.')}
        </div>
      </Section>
    </div>
  )
}
